/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {useParams} from 'react-router-dom'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import Slider from 'react-slick'

import {
    Box,
    Container,
    Flex,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {withCommerceSdkReact} from '@salesforce/retail-react-app/app/components/with-commerce-sdk-react/with-commerce-sdk-react'
import {useCategory} from '@salesforce/commerce-sdk-react'
import {BlackArrow} from '../../components/icons'
import {subcategoryCarousel} from '../../theme/components/project/sub-category-carousel'
import {categoryUrlBuilder} from '../../utils/url'

/** Placeholder shown while category data loads */
const PlaceholderComponent = () => (
    <Box width="100px" height="120px" background="gray.100" borderRadius="8px" />
)

/** Item component with data fetched via SDK */
const SubCategoryItem = withCommerceSdkReact(
    ({data}) => {
        const {locale} = useIntl()

        if (!data?.c_showCategoryImage && !data?.image) return null

        return (
            <Flex
                flexDirection="column"
                alignItems="center"
                as={Link}
                to={categoryUrlBuilder(data, locale)}
            >
                <DynamicImage
                    src={data?.c_image || data?.image}
                    imageProps={{
                        alt: data?.name || 'Category',
                        loading: 'eager',
                        borderRadius: 16,
                        marginBottom: {base: 2, md: 4}
                    }}
                />
                <Text
                    fontSize={{base: 16, md: 20}}
                    fontWeight="800"
                    lineHeight={{base: 5, md: 7}}
                    textAlign="center"
                    whiteSpace="normal"
                >
                    {data?.name}
                </Text>
            </Flex>
        )
    },
    {
        hook: useCategory,
        queryOptions: ({data}) => ({
            parameters: {
                id: data.id,
                levels: 2
            }
        }),
        placeholder: PlaceholderComponent
    }
)

const SubCategoryCarousel = () => {
    const {categoryId} = useParams()

    const {data: categoryData} = useCategory({
        parameters: {
            id: categoryId,
            levels: 2
        }
    })

    const items = categoryData?.categories || []

    const filteredItems = items.flatMap((item) => {
        const parent = item?.c_showCategoryImage ? [item] : []
        const children = item?.categories?.filter((child) => child?.c_showCategoryImage) || []
        return [...parent, ...children]
    })

    const NextArrow = (props) => {
        const {className, onClick} = props
        return (
            <Box
                className={className}
                onClick={onClick}
                role="button"
                onKeyDown={(e) => e.key === 'Enter' && onClick()}
                tabIndex={0}
                position={'absolute'}
                right={{base: '-30px', xl: '-15px'}}
            >
                <BlackArrow />
            </Box>
        )
    }

    const PrevArrow = (props) => {
        const {className, onClick} = props
        return (
            <Box
                className={className}
                onClick={onClick}
                role="button"
                onKeyDown={(e) => e.key === 'Enter' && onClick()}
                tabIndex={0}
                transform="rotate(180deg)"
                position={'absolute'}
                left={'-45px'}
            >
                <BlackArrow />
            </Box>
        )
    }

    const sliderSettings = {
        infinite: false,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 1,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        arrows: true,
        responsive: [
            {
                breakpoint: 1023,
                settings: {
                    slidesToShow: 5,
                    arrows: false
                }
            },
            {
                breakpoint: 767,
                settings: {
                    slidesToShow: 3,
                    arrows: false
                }
            }
        ]
    }

    return (
        <Container as={Stack} p={0}>
            <Box className="carousel-wrapper container-flex">
                {filteredItems?.length > 0 && (
                    <Box
                        sx={subcategoryCarousel}
                        data-testid="sub-category-carousel"
                        w={'100%'}
                        marginBottom={{base: 4, md: 8}}
                    >
                        <Slider {...sliderSettings} className="carousel-sg">
                            {filteredItems.map((item, index) => (
                                <Box key={item?.id || index} pr={{base: 2, sm: 3, md: 4, xl: 8}}>
                                    <SubCategoryItem
                                        key={item?.id || index}
                                        data={item}
                                        data-testid="sub-category-item"
                                    />
                                </Box>
                            ))}
                        </Slider>
                    </Box>
                )}
            </Box>
        </Container>
    )
}

SubCategoryCarousel.propTypes = {
    item: PropTypes.object,
    itemsKey: PropTypes.string,
    className: PropTypes.string,
    onClick: PropTypes.func
}

export {SubCategoryCarousel}
